/**
 * Tabellone della SquadCup (Andrea 2026-10-08): turni in colonna su desktop,
 * impilati su telefono. Ogni sfida: loghi, nomi, lega, fantapunti di andata
 * e ritorno, totale; la vincente in oro.
 */

import { TeamBadge } from "../common/TeamBadge";
import type { SquadCupDettaglio, SquadCupPartita, SquadraBreve } from "../../hooks/useSquadCup";
import { tr } from "../../i18n";

function fp(v: string | null): string {
  return v === null ? "—" : Number(v).toFixed(1);
}

function totale(a: string | null, r: string | null): string {
  if (a === null && r === null) return "—";
  return (Number(a ?? 0) + Number(r ?? 0)).toFixed(1);
}

function RigaSquadra({
  s,
  andata,
  ritorno,
  conRitorno,
  vincente,
  decisa,
}: {
  s: SquadraBreve;
  andata: string | null;
  ritorno: string | null;
  conRitorno: boolean;
  vincente: boolean;
  decisa: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 ${
        decisa && !vincente ? "opacity-50" : ""
      } ${vincente ? "bg-accent/10" : ""}`}
    >
      <TeamBadge nome={s.nome} logoDataUrl={s.logo_data_url} size={28} variant="home" />
      <div className="min-w-0 flex-1">
        <div className={`text-sm truncate ${vincente ? "font-bold text-accent" : "text-fg"}`}>
          {s.nome}
        </div>
        <div className="text-[10px] text-fg-muted truncate">{s.lega_nome}</div>
      </div>
      <div className="font-mono text-xs text-fg-muted tabular-nums text-right">
        {conRitorno ? (
          <>
            <span>{fp(andata)}</span>
            <span className="mx-1">·</span>
            <span>{fp(ritorno)}</span>
          </>
        ) : null}
      </div>
      <div className="font-mono text-base font-bold tabular-nums w-12 text-right">
        {conRitorno ? totale(andata, ritorno) : fp(andata)}
      </div>
    </div>
  );
}

function CardPartita({ p, conRitorno }: { p: SquadCupPartita; conRitorno: boolean }) {
  const decisa = p.vincitore_squadra_id !== null;
  return (
    <div className="rounded-card border border-primary/30 bg-bg-card divide-y divide-primary/20 overflow-hidden">
      <RigaSquadra
        s={p.casa}
        andata={p.fp_casa_andata}
        ritorno={p.fp_casa_ritorno}
        conRitorno={conRitorno}
        vincente={p.vincitore_squadra_id === p.casa.id}
        decisa={decisa}
      />
      <RigaSquadra
        s={p.trasferta}
        andata={p.fp_trasferta_andata}
        ritorno={p.fp_trasferta_ritorno}
        conRitorno={conRitorno}
        vincente={p.vincitore_squadra_id === p.trasferta.id}
        decisa={decisa}
      />
    </div>
  );
}

export function Tabellone({ ed }: { ed: SquadCupDettaglio }) {
  if (ed.turni.length === 0) {
    return (
      <div className="card text-sm text-fg-muted">
        Il sorteggio non è ancora stato fatto. Partecipanti iscritti: {ed.n_partecipanti}.
      </div>
    );
  }
  return (
    <div className="grid gap-4 md:grid-flow-col md:auto-cols-fr">
      {ed.turni.map((t) => {
        const conRitorno = t.giornata_sa_ritorno !== null;
        return (
          <section key={t.id} className="space-y-2">
            <div className="text-center">
              <div className="font-display text-base text-accent tracking-wide">{t.nome}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-fg-muted">
                {conRitorno
                  ? tr("Serie A {{v1}}ª · {{v2}}ª", {
                      v1: t.giornata_sa_andata,
                      v2: t.giornata_sa_ritorno,
                    })
                  : tr("Serie A {{v1}}ª · gara secca", { v1: t.giornata_sa_andata })}
              </div>
            </div>
            {t.partite.length === 0 ? (
              <div className="rounded-card border border-dashed border-primary/30 p-3 text-center text-xs text-fg-muted">
                {tr("In attesa del turno precedente")}
              </div>
            ) : (
              t.partite.map((p) => <CardPartita key={p.id} p={p} conRitorno={conRitorno} />)
            )}
          </section>
        );
      })}
    </div>
  );
}
